// Maps semantic alias names → SF Symbol dot-notation names.
// Use these aliases in <SFIcon name="..." /> — never use raw SF Symbol names in component code.
// To add a new icon: add the SVG to src/assets/icons/sf-symbols/, register it in
// src/assets/icons/sf-symbols/index.js, then add the alias here.

export const ICON_ALIASES = {
  // ── Actions ──────────────────────────────────────────────────────────────────
  add:              'plus',
  addFill:          'plus.circle.fill',
  close:            'xmark',
  closeFill:        'xmark.circle.fill',
  clearSearch:      'xmark.circle.fill',
  search:           'magnifyingglass',
  edit:             'square.and.pencil',
  delete:           'trash',
  retry:            'arrow.clockwise',
  send:             'arrow.up.circle.fill',
  share:            'square.and.arrow.up',
  scan:             'barcode.viewfinder',
  scanQR:           'qrcode.viewfinder',
  more:             'ellipsis',
  moreCircle:       'ellipsis.circle',
  moreCircleFill:   'ellipsis.circle.fill',
  eye:              'eye',
  lock:             'lock.fill',
  addTech:          'person.badge.plus',
  printer:          'printer',

  // ── Navigation ───────────────────────────────────────────────────────────────
  back:             'chevron.left',
  forward:          'chevron.right',
  expand:           'chevron.down',
  collapse:         'chevron.up',
  sectionExpanded:  'chevron.left.circle',
  sectionCollapsed: 'chevron.right.circle',

  // ── States & Indicators ──────────────────────────────────────────────────────
  quickModeOn:      'bolt.fill',
  quickModeOff:     'bolt.slash.fill',
  flashlightOn:     'bolt.fill',
  flashlightOff:    'flashlight.off.fill',
  torchOff:         'bolt.slash',
  deviceWorking:    'checkmark.circle.fill',
  deviceNotWorking: 'x.circle.fill',
  confirm:          'checkmark',
  confirmCircle:    'checkmark.circle',
  confirmFill:      'checkmark.circle.fill',
  unselected:       'circle',
  warning:          'exclamationmark.triangle.fill',
  info:             'info.circle',
  flag:             'flag',
  flagActive:       'flag.fill',
  star:             'star.fill',
  sparkles:         'sparkles',
  metrics:          'waveform.path.ecg.rectangle',
  // No dedicated thumbsdown asset exists — SupportChat flips this one via
  // CSS (transform: scaleY(-1)) rather than adding a new SVG for a single
  // use. Revisit if a real hand.thumbsdown symbol gets added later.
  thumbsUp:         'hand.thumbsup',
  attachImage:      'camera',

  // ── Location & People ────────────────────────────────────────────────────────
  store:            'building.2',
  location:         'mappin.circle.fill',
  unknownCustomer:  'person.crop.circle.badge.questionmark',

  // ── Financial ────────────────────────────────────────────────────────────────
  banknote:         'banknote',
  percent:          'percent',
  subtract:         'minus',
  moneyOut:         'arrow.up.circle.fill',
  moneyIn:          'arrow.down.circle.fill',
  repair:           'wrench.and.screwdriver',
  orders:           'cart',
  salesChart:       'chart.bar',

  // ── Payment Methods ──────────────────────────────────────────────────────────
  paymentCash:      'banknote',
  paymentOther:     'banknote',
  paymentCredit:    'creditcard',
  paymentCard:      'creditcard.fill',
  paymentSplit:     'percent',
  paymentQueue:     'arrow.up.circle.fill',
  paymentInvoice:   'square.and.arrow.up',
  paymentManual:    'square.and.pencil',

  // ── Empty States ─────────────────────────────────────────────────────────────
  emptyFolder:      'folder.badge.questionmark',
  emptySearch:        'magnifyingglass',
  emptyRepairs:       'wrench.and.screwdriver',
  emptyCustomers:     'person.crop.circle.badge.questionmark',
  emptyInventory:     'shippingbox',
  emptyOrders:        'cart',
  emptySales:         'dollarsign.circle',
  emptyEmployees:     'person.badge.plus',
  emptyAppointments:  'clock.badge.questionmark',
  filter:           'line.3.horizontal.decrease.circle',

  // ── Repair Statuses (Detail / Update Status screen) ─────────────────────────
  statusRepaired:          'checkmark.circle',
  statusInProgress:        'wrench.adjustable',
  statusAwaitingParts:     'clock.badge.questionmark',
  statusWaitingOnCustomer: 'person.badge.clock',
  statusShipped:           'shippingbox',
  statusDelivered:         'checkmark.seal',
  statusPickedUp:          'hand.thumbsup',
  statusTechDamage:        'exclamationmark.triangle',
  statusCanceled:          'xmark.circle',
  statusNonRepairable:     'nosign',
  statusPaid:              'dollarsign.circle',
  statusPending:           'clock',
  // ── Device Types ─────────────────────────────────────────────────────────────
  deviceIphone:     'smartphone',
  deviceIpad:       'ipad.rear.camera',
  deviceWatch:      'watch.analog',
  deviceAirpods:    'headphones',
  deviceLaptop:     'laptopcomputer',
  deviceDesktop:    'desktopcomputer',
  deviceTV:         'tv',
  deviceCamera:     'camera',
  devicePrinter:    'printer',
  deviceSpeaker:    'hifispeaker',
  deviceGaming:     'gamecontroller',
  deviceIpod:       'smartphone',
  deviceGeneric:    'wrench.and.screwdriver',

  // ── Repair Types ────────────────────────────────────────────────────────────
  repairTypePhone:      'smartphone',
  repairTypeTablet:     'ipad.rear.camera',
  repairTypeWatch:      'watch.analog',
  repairTypeIpod:       'smartphone',
  repairTypeComputer:   'desktopcomputer',
  repairTypeLaptop:     'laptopcomputer',
  repairTypeTV:         'tv',
  repairTypeAirpods:    'headphones',
  repairTypeHeadphones: 'headphones',
  repairTypeConsole:    'gamecontroller',
  repairTypeCamera:     'camera',
  repairTypeDefault:    'wrench.and.screwdriver',

  // ── Communication & Scheduling ───────────────────────────────────────────────
  aptDate:              'calendar',
  email:                'envelope',
  phone:                'phone',
  notes:                'text.pad.header',

  // ── Layout ───────────────────────────────────────────────────────────────────
  boardView:            'rectangle.split.3x1',

  // ── Repair Detail Quick Actions ──────────────────────────────────────────────
  checkout:             'cart',
  deposit:              'banknote',
  updateWarrantyPeriod: 'checkmark.shield',
  partsAndAccessories:  'wrench.and.screwdriver',
  addNote:              'text.pad.header',
  addDiagnostics:       'stethoscope',
  notifyCustomer:       'bell.badge',
  remindPayment:        'exclamationmark.bubble',
  updatePriorityStatus: 'flag.fill',
  updateRepairStatus:   'arrow.clockwise',
  printRepairEstimate:  'list.clipboard',
  manageOrders:         'shippingbox',
  transferRepair:       'arrow.left.arrow.right',
  lockPattern:          'circle.grid.3x3.fill',
  submitWarranty:       'wrench.and.screwdriver',

  // ── Buybacks (Inventory → Buybacks) ─────────────────────────────────────────
  buybackReport:        'chart.bar',
  buybackSettings:      'gearshape.2.fill',
  buybackQuote:         'text.pad.header',
  buybackPaid:          'checkmark.circle.fill',
  buybackVoided:        'xmark.circle',
  buybackOnHold:        'clock',
  buybackRefurbishing:  'wrench.and.screwdriver',
  buybackForSale:       'dollarsign.circle',
  buybackSold:          'checkmark.seal',
  buybackInStock:       'shippingbox',
  buybackPending:       'clock.badge.questionmark',
  buybackRemoved:       'nosign',
  buybackItems:         'shippingbox.fill',
  buybackSellerId:      'person.2',
  buybackChecklist:     'checkmark.circle',
  buybackPriceList:     'text.pad.header',
  buybackPart:          'wrench.adjustable',
  buybackLabel:         'barcode',
  buybackImeiCheck:     'checkmark.shield',
  swap:                 'arrow.left.arrow.right',

  // ── Inventory tiles (match the iOS Inventory screen) ────────────────────────
  inventoryCount:       'list.bullet.rectangle.portrait',
  deviceManagement:     'laptopcomputer.and.iphone',
  buybacks:             'dollarsign.arrow.circlepath',
  itemStatusLog:        'clock.arrow.circlepath',

  // ── Help Center groups ───────────────────────────────────────────────────────
  // One alias per topic a Help `iconId` can resolve to. The iconId → alias
  // lookup itself lives in components/Help/helpIcons.js; these are only the
  // aliases that lookup is allowed to return.
  helpGettingStarted:   'sparkles',
  helpRepairs:          'wrench.and.screwdriver',
  helpTickets:          'text.pad.header',
  helpSales:            'dollarsign.circle',
  helpOrders:           'cart',
  helpPayments:         'creditcard',
  helpBilling:          'banknote',
  helpSubscription:     'star.fill',
  helpInventory:        'shippingbox',
  helpDevices:          'smartphone',
  helpCustomers:        'person.2',
  helpEmployees:        'person.badge.plus',
  helpAppointments:     'calendar',
  helpReports:          'chart.bar',
  helpSettings:         'gearshape.2.fill',
  helpAccount:          'lock.fill',
  helpStore:            'building.2',
  helpIntegrations:     'arrow.left.arrow.right',
  helpNotifications:    'envelope',
  helpMarketing:        'paperplane.fill',
  helpShipping:         'airplane.departure',
  helpPrinters:         'printer',
  helpBarcodes:         'barcode',
  helpPricing:          'percent',
  helpWarranty:         'checkmark.shield',
  helpDiagnostics:      'stethoscope',
  helpSecurity:         'lock.fill',
  helpGeneral:          'info.circle',
};
